/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1792540800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Wed Oct 21 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "sfmc",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Salesforce Marketing Cloud setup",
          "depth": 0,
          "id": "salesforce-marketing-cloud-setup"
        }, {
          "value": "Create installed package in SFMC",
          "depth": 1,
          "id": "create-installed-package-in-sfmc"
        }, {
          "value": "Create FTP user",
          "depth": 1,
          "id": "create-ftp-user"
        }, {
          "value": "Connect to SFMC",
          "depth": 0,
          "id": "connect-to-sfmc"
        }, {
          "value": "Authentication",
          "depth": 1,
          "id": "authentication"
        }, {
          "value": "Client secret rotation",
          "depth": 1,
          "id": "client-secret-rotation"
        }, {
          "value": "FTP credentials",
          "depth": 1,
          "id": "ftp-credentials"
        }, {
          "value": "File location",
          "depth": 1,
          "id": "file-location"
        }, {
          "value": "Custom scopes",
          "depth": 1,
          "id": "custom-scopes"
        }, {
          "value": "Allowed Business Units",
          "depth": 1,
          "id": "allowed-business-units"
        }, {
          "value": "Use SFMC with agents",
          "depth": 0,
          "id": "use-sfmc-with-agents"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Data Extensions",
          "depth": 1,
          "id": "data-extensions"
        }, {
          "value": "Contacts",
          "depth": 1,
          "id": "contacts"
        }, {
          "value": "Journeys",
          "depth": 1,
          "id": "journeys"
        }, {
          "value": "Triggered send",
          "depth": 1,
          "id": "triggered-send"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Validate your setup",
          "depth": 1,
          "id": "validate-your-setup"
        }, {
          "value": "Choosing a sync method for Data Extensions",
          "depth": 1,
          "id": "choosing-a-sync-method-for-data-extensions"
        }, {
          "value": "DateTime timezone conversions",
          "depth": 1,
          "id": "datetime-timezone-conversions"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h1: "h1",
      p: "p",
      ul: "ul",
      li: "li",
      h2: "h2",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      a: "a",
      strong: "strong",
      h3: "h3",
      ol: "ol",
      img: "img",
      h4: "h4",
      code: "code",
      em: "em",
      pre: "pre",
      span: "span"
    }, _provideComponents(), props.components), {PremiumDestination, CommonErrorsPreface, LiveDebugger, SyncAlerts} = _components;
    if (!CommonErrorsPreface) _missingMdxReference("CommonErrorsPreface", true);
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!PremiumDestination) _missingMdxReference("PremiumDestination", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(PremiumDestination, {
        destinationName: "Salesforce Marketing Cloud"
      }), "\n", _jsx(_components.h1, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "Salesforce Marketing Cloud (SFMC) is a digital marketing platform that enables businesses to create personalized customer journeys across email, mobile, social, web, and more. This integration allows you to sync your data from Hightouch to SFMC to power personalized marketing campaigns, manage contact data, automate customer journeys, and trigger communications."
      }), "\n", _jsx(_components.p, {
        children: "With the Hightouch SFMC integration, you can:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Sync data to SFMC Data Extensions for segmentation and personalization"
        }), "\n", _jsx(_components.li, {
          children: "Update contact information for better targeting"
        }), "\n", _jsx(_components.li, {
          children: "Manage entry and exit from customer journeys based on your data"
        }), "\n", _jsx(_components.li, {
          children: "Trigger email and SMS messages based on customer behaviors"
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "This documentation will guide you through setting up the connection, configuring syncs, and troubleshooting common issues."
      }), "\n", _jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              align: "left",
              children: "Sync Type"
            }), _jsx(_components.th, {
              align: "left",
              children: "Description"
            }), _jsx(_components.th, {
              align: "left",
              children: "Supported Sync Modes"
            }), _jsx(_components.th, {
              align: "left",
              children: "API Reference"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.a, {
                href: "#data-extensions",
                children: _jsx(_components.strong, {
                  children: "Data Extensions"
                })
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "Sync records to new or existing Data Extensions, using the SOAP API and optionally file uploads"
            }), _jsx(_components.td, {
              align: "left",
              children: "Upsert, Mirror"
            }), _jsx(_components.td, {
              align: "left",
              children: _jsx(_components.a, {
                href: "https://help.salesforce.com/s/articleView?id=sf.mc_co_salesforce_data_extensions.htm&language=en_US&type=5",
                children: "Manage Data Extensions ↗"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.a, {
                href: "#contacts",
                children: _jsx(_components.strong, {
                  children: "Contacts"
                })
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "Sync records to Marketing Cloud contacts"
            }), _jsx(_components.td, {
              align: "left",
              children: "Upsert"
            }), _jsx(_components.td, {
              align: "left",
              children: _jsx(_components.a, {
                href: "https://developer.salesforce.com/docs/marketing/marketing-cloud/guide/soap_web_service_objects.html",
                children: "Manage SOAP API objects ↗"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.a, {
                href: "#journeys",
                children: _jsx(_components.strong, {
                  children: "Journeys"
                })
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "Trigger API events to enter and exit contacts from journeys as they're added to and removed from your query result"
            }), _jsx(_components.td, {
              align: "left",
              children: "Add, Remove"
            }), _jsxs(_components.td, {
              align: "left",
              children: [_jsx(_components.a, {
                href: "https://developer.salesforce.com/docs/marketing/marketing-cloud/guide/how-to-fire-an-event.html?q=entry",
                children: "Fire entry events ↗"
              }), " and ", _jsx(_components.a, {
                href: "https://developer.salesforce.com/docs/marketing/marketing-cloud/guide/contactExitRequest.html",
                children: "exit contacts ↗"
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.a, {
                href: "#triggered-send",
                children: _jsx(_components.strong, {
                  children: "Triggered send"
                })
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "Send email and SMS messages to individual subscribers"
            }), _jsx(_components.td, {
              align: "left",
              children: "Trigger"
            }), _jsx(_components.td, {
              align: "left",
              children: _jsx(_components.a, {
                href: "https://developer.salesforce.com/docs/marketing/marketing-cloud/guide/creating_a_triggered_send_using_the_async_api.html",
                children: "Create triggered sends ↗"
              })
            })]
          })]
        })]
      }), "\n", _jsx(_components.h2, {
        id: "salesforce-marketing-cloud-setup",
        children: "Salesforce Marketing Cloud setup"
      }), "\n", _jsx(_components.p, {
        children: "Before you connect Hightouch to Salesforce Marketing Cloud (SFMC), you need to create an installed package in SFMC for Hightouch to use."
      }), "\n", _jsxs(_components.p, {
        children: ["If you want to sync records to Data Extensions using ", _jsx(_components.a, {
          href: "#data-extensions",
          children: "either mirror mode or upsert mode with file uploads"
        }), ", you also need to ", _jsx(_components.a, {
          href: "#create-ftp-user",
          children: "create an FTP user"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "create-installed-package-in-sfmc",
        children: "Create installed package in SFMC"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Log in to your SFMC instance. Go to ", _jsx(_components.strong, {
            children: "Setup"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-sfmc-setup.png",
          alt: "Opening Setup in SFMC"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "2",
        children: ["\n", _jsxs(_components.li, {
          children: ["In the left sidebar, go to ", _jsx(_components.strong, {
            children: "Apps → Installed Packages"
          }), " and click ", _jsx(_components.strong, {
            children: "New"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-sfmc-new-package.png",
          alt: "Package creation in SFMC"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "3",
        children: ["\n", _jsxs(_components.li, {
          children: ["Name your package, for example, \"Hightouch Integration.\" Optionally provide a description, and click ", _jsx(_components.strong, {
            children: "Save"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-sfmc-new-package-2.png",
          alt: "Package creation in SFMC"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "4",
        children: ["\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Add Component"
          }), " and select ", _jsx(_components.strong, {
            children: "API Integration"
          }), " as the component type. Click ", _jsx(_components.strong, {
            children: "Next"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-sfmc-add-component.png",
          alt: "Package configuration in SFMC"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "5",
        children: ["\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Server-to-Server"
          }), " as the integration type and click ", _jsx(_components.strong, {
            children: "Next"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-sfmc-add-component-type.png",
          alt: "Package configuration in SFMC"
        })
      }), "\n", _jsx(_components.h4, {
        id: "permissions",
        children: "Permissions"
      }), "\n", _jsx(_components.p, {
        children: "These are the minimum scopes for syncing to Contacts, Journeys, and Data Extensions."
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "Carefully select the appropriate permissions based on your specific use case."
        })
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Automation > Journeys"
          }), ": Read, Write, Execute, Activate/Stop/Pause/Resume/Send/Schedule"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Contacts > List and subscribers"
          }), ": Read, Write"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Data > Data Extensions"
          }), ": Read, Write"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Provisioning > Accounts"
          }), ": Read"]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-sfmc-automation-scope.png",
          alt: "Package configuration in SFMC"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["If you want to sync to ", _jsx(_components.a, {
          href: "#data-extensions",
          children: "Data Extensions"
        }), " using mirror mode or upsert mode with\n", _jsx(_components.a, {
          href: "#automatic-file-import-via-file-uploads",
          children: "file uploads"
        }), ", grant these additional permissions:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Data > File Locations"
          }), ": Read, Write"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Automation > Automations"
          }), ": Read, Write, Execute"]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "If you want to trigger sends, grant these additional permissions:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Channels > Email"
          }), ": Read"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Channels > SMS"
          }), ": Read"]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["If you would like to grant a custom subset of scopes for just the SFMC\nfeatures you want to use, see the section on ", _jsx(_components.a, {
          href: "#custom-scopes",
          children: "custom scopes"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "Once you save your permissions, you should see a new API Integration component in your package."
      }), "\n", _jsxs(_components.p, {
        children: ["With the API Integration created, copy the ", _jsx(_components.strong, {
          children: "Client ID"
        }), ", ", _jsx(_components.strong, {
          children: "Client Secret"
        }), ", and subdomain to enter into Hightouch."]
      }), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["The subdomain is the part of the ", _jsx(_components.strong, {
            children: "Authentication base URI"
          }), " after ", _jsx(_components.code, {
            children: "https://"
          }), "\nand before ", _jsx(_components.code, {
            children: ".auth.marketingcloudapis.com/"
          }), ". For example, if your\nAuthentication base URI is\n", _jsx(_components.code, {
            children: "https://mc6vgk-yfjd8879pq4v.auth.marketingcloudapis.com/"
          }), ", your subdomain is\n", _jsx(_components.code, {
            children: "mc6vgk-yfjd8879pq4v"
          }), "."]
        })
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-sfmc-base-uri.png",
          alt: "Package configuration in SFMC"
        })
      }), "\n", _jsx(_components.h3, {
        id: "create-ftp-user",
        children: "Create FTP user"
      }), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["You only need to create a FTP user if want to sync ", _jsx(_components.a, {
            href: "#data-extensions",
            children: "Data\nExtensions"
          }), " via file uploads. Using File Upload mode can\noptimize sync performance, especially on high volume syncs (over 1 million\nrecords). Learn more about how file upload protocol works and its tradeoffs in\nthe ", _jsx(_components.a, {
            href: "#automatic-file-import-via-file-uploads",
            children: "file uploads section"
          }), ". You will\nneed a SFMC FTP user even if you are using a blob storage file location."]
        })
      }), "\n", _jsx(Warning, {
        children: _jsx(_components.p, {
          children: "If your SFMC instance has parent-child Business Units and you want to sync to\na child Business Unit using FTP, you must create the FTP user in the child\nBusiness Unit. Creating the FTP user in the parent Business Unit will not work\nfor syncing to child Business Units."
        })
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["In your SFMC instance, go to ", _jsx(_components.strong, {
            children: "Setup → Administration → Data Management → FTP Accounts"
          }), " and click ", _jsx(_components.strong, {
            children: "Create User"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-sfmc-new-sftp-user.png",
          alt: "FTP User creation in SFMC"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "2",
        children: ["\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["By default, the ", _jsx(_components.strong, {
              children: "FTP Username"
            }), " is your ", _jsx(_components.a, {
              href: "https://trailhead.salesforce.com/content/learn/modules/marketing-cloud-developer-basics/learn-administration-basics",
              children: "Marketing Cloud Member ID (MID)"
            }), ", including the current parent account or child business unit. You can keep the default value."]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Enter an ", _jsx(_components.strong, {
              children: "Email Address"
            }), " for the new FTP user. This can be the email account you use to log in to SFMC or another one. You won't need this address to connect to Hightouch."]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Enter a ", _jsx(_components.strong, {
              children: "Password"
            }), " for the user. Password complexity requirements combine Marketing Cloud password policy and server-side FTP password requirements. These policies require a minimum of 12 characters and no reuse of the five most recent passwords. Reenter the password in ", _jsx(_components.strong, {
              children: "Repeat Password"
            }), "."]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsx(_components.p, {
            children: "The next screen prompts you to choose your authentication method."
          }), "\n", _jsxs(_components.ul, {
            children: ["\n", _jsxs(_components.li, {
              children: ["\n", _jsxs(_components.p, {
                children: ["If you want to use a password, keep this selection.\n", _jsx(_components.img, {
                  src: "destinations/destination-sfmc-new-sftp-user-authentication-2.png",
                  alt: "FTP User creation in SFMC"
                })]
              }), "\n"]
            }), "\n", _jsxs(_components.li, {
              children: ["\n", _jsxs(_components.p, {
                children: ["If you want to authenticate Hightouch to SFMC using an SSH key, select ", _jsx(_components.strong, {
                  children: "SSH Key"
                }), ". Follow ", _jsx(_components.a, {
                  href: "https://help.salesforce.com/s/articleView?id=sf.mc_overview_key_create_ssh_for_ftp.htm&type=5",
                  children: "SFMC's instructions"
                }), " for how to add a key. Enter the public key into the SFMC and keep the private key for when you ", _jsx(_components.a, {
                  href: "#connect-to-sfmc",
                  children: "connect to Hightouch"
                }), ".\n", _jsx(_components.img, {
                  src: "destinations/destination-sfmc-new-sftp-user-authentication-3.png",
                  alt: "FTP User creation in SFMC"
                })]
              }), "\n"]
            }), "\n"]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["On the next screen, select permissions for your FTP user. At a minimum, provide the user ", _jsx(_components.strong, {
              children: "Upload"
            }), " access to the ", _jsx(_components.strong, {
              children: "Import"
            }), " directory."]
          }), "\n"]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-sfmc-ftp-permissions.png",
          alt: "FTP permissions in SFMC"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "7",
        children: ["\n", _jsxs(_components.li, {
          children: ["Allowlisting IP addresses is optional. For additional security, you can enter ", _jsx(_components.a, {
            href: "/security/networking#ip-addresses",
            children: "Hightouch's IP addresses"
          }), ". Click ", _jsx(_components.strong, {
            children: "Next"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select a ", _jsx(_components.strong, {
            children: "Home Folder"
          }), ". We recommend setting the home folder to the root directory. If you choose to specify a restricted scope, ensure that this FTP user has access to the ", _jsx(_components.strong, {
            children: "Import"
          }), " directory."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-sfmc-ftp-home-folder.png",
          alt: "FTP home folder in SFMC"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "9",
        children: ["\n", _jsxs(_components.li, {
          children: ["Make sure to copy your FTP Username and either password or SSH private key for entry into Hightouch. Click ", _jsx(_components.strong, {
            children: "Save"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-sfmc",
        children: "Connect to SFMC"
      }), "\n", _jsxs(_components.p, {
        children: ["You can now connect SFMC to Hightouch. Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations?from=docs",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add destination"
        }), " button.\nSelect ", _jsx(_components.strong, {
          children: "Salesforce Marketing Cloud"
        }), " and click ", _jsx(_components.strong, {
          children: "Continue"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "authentication",
        children: "Authentication"
      }), "\n", _jsxs(_components.p, {
        children: ["You can authenticate Hightouch to SFMC by entering the ", _jsx(_components.strong, {
          children: "Client ID"
        }), ", ", _jsx(_components.strong, {
          children: "Client Secret"
        }), ", and ", _jsx(_components.strong, {
          children: "Subdomain"
        }), " you copied during ", _jsx(_components.a, {
          href: "#create-installed-package-in-sfmc",
          children: "package creation"
        }), "."]
      }), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["The subdomain is the part of the ", _jsx(_components.strong, {
            children: "Authentication base URI"
          }), " after ", _jsx(_components.code, {
            children: "https://"
          }), "\nand before ", _jsx(_components.code, {
            children: ".auth.marketingcloudapis.com/"
          }), ". For example, if your\nAuthentication base URI is\n", _jsx(_components.code, {
            children: "https://mc6vgk-y5h9sgf6c5t8vxs09pq4v.auth.marketingcloudapis.com/"
          }), ", your\nsubdomain is ", _jsx(_components.code, {
            children: "mc6vgk-y5h9sgf6c5t8vxs09pq4v"
          }), "."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "client-secret-rotation",
        children: "Client secret rotation"
      }), "\n", _jsx(Warning, {
        children: _jsxs(_components.p, {
          children: ["SFMC client secrets expire 180 days after they're generated, and Salesforce\nhas announced that all client secrets created before this policy took effect\nexpire on September 30, 2026. When your secret expires, all syncs to this\ndestination fail with an ", _jsx(_components.code, {
            children: "invalid_client"
          }), " authentication error until you\nrotate the secret. See ", _jsx(_components.a, {
            href: "https://developer.salesforce.com/docs/marketing/marketing-cloud/guide/rotate-oauth2-secret.html",
            children: "Salesforce's rotation\nguide"
          }), "\nfor details."]
        })
      }), "\n", _jsx(_components.p, {
        children: "Salesforce doesn't provide an API for rotating client secrets, so you must rotate them in the SFMC interface. SFMC accepts both the staged and the active secret until you activate the staged one, so you can rotate without downtime:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["In SFMC, go to ", _jsx(_components.strong, {
            children: "Setup → Apps → Installed Packages"
          }), " and open the package Hightouch uses. In the ", _jsx(_components.strong, {
            children: "Staged Secret"
          }), " section, select ", _jsx(_components.strong, {
            children: "Generate"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["In Hightouch, enter the new secret in the ", _jsx(_components.strong, {
            children: "Client Secret"
          }), " field in the ", _jsx(_components.a, {
            href: "https://app.hightouch.com/destinations?from=docs",
            children: "destination's configuration"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["In SFMC, select ", _jsx(_components.strong, {
            children: "Activate"
          }), " on the staged secret."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["To see expiration dates for all of your client secrets, check the summary table on the ", _jsx(_components.strong, {
          children: "Installed Packages"
        }), " page in SFMC Setup."]
      }), "\n", _jsx(_components.h3, {
        id: "ftp-credentials",
        children: "FTP credentials"
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["This section is only required if you want to ", _jsx(_components.a, {
            href: "#automatic-file-import-via-file-uploads",
            children: "sync records to Data Extensions\nusing mirror mode or upsert mode with file\nuploads"
          }), "."]
        })
      }), "\n", _jsx(_components.p, {
        children: "Enter your FTP user credentials:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "FTP Username"
          }), ": typically a numeric Marketing Cloud MID"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Password"
          }), " ", _jsx(_components.em, {
            children: "or"
          }), " ", _jsx(_components.strong, {
            children: "private key"
          }), "\n", _jsxs(_components.ul, {
            children: ["\n", _jsxs(_components.li, {
              children: ["To use a private key, you must have ", _jsx(_components.a, {
                href: "#create-ftp-user",
                children: "uploaded the corresponding public key in SFMC"
              })]
            }), "\n", _jsxs(_components.li, {
              children: ["If using a private key, you must enter the complete private key, starting with ", _jsx(_components.code, {
                children: "-----BEGIN...PRIVATE KEY-----"
              }), "."]
            }), "\n", _jsx(_components.li, {
              children: "If your private key requires a passphrase, enter it as well"
            }), "\n"]
          }), "\n"]
        }), "\n"]
      }), "\n", _jsx(Warning, {
        children: _jsx(_components.p, {
          children: "Incorrect FTP credentials will cause file upload syncs to fail. Double-check\nyour credentials if you encounter connection issues."
        })
      }), "\n", _jsx(_components.h3, {
        id: "file-location",
        children: "File location"
      }), "\n", _jsx(Tip, {
        children: _jsx(_components.p, {
          children: "You only need to set up a file location in SFMC if you are syncing Data\nExtensions with file uploads."
        })
      }), "\n", _jsx(_components.p, {
        children: "Hightouch supports the following file location types:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "ExactTarget Enhanced FTP Import Directory (default)"
        }), "\n", _jsx(_components.li, {
          children: "Relative Location under FTP Site"
        }), "\n", _jsx(_components.li, {
          children: "Google Cloud Storage"
        }), "\n", _jsx(_components.li, {
          children: "Amazon S3"
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "exacttarget-enhanced-ftp-import-directory",
        children: "ExactTarget Enhanced FTP Import Directory"
      }), "\n", _jsxs(_components.p, {
        children: ["For ", _jsx(_components.strong, {
          children: "ExactTarget Enhanced FTP"
        }), ", if you have chosen a different External Key for this location, enter the External Key to ensure Hightouch is able to access the uploaded files."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-sfmc-exacttarget-enhanced-ftp-file-location.png",
          alt: "ExactTarget Enhanced FTP - File Location External Key"
        })
      }), "\n", _jsx(_components.h4, {
        id: "relative-location-under-ftp-site",
        children: "Relative Location under FTP Site"
      }), "\n", _jsxs(_components.p, {
        children: ["For ", _jsx(_components.strong, {
          children: "Relative Location under FTP Site"
        }), ", enter the Relative Folder Path and the External Key to ensure Hightouch is able to access the uploaded files."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-sfmc-relative-location-for-ftp-site-file-location.png",
          alt: "Relative Location under FTP Site - File Location and External Key"
        })
      }), "\n", _jsx(Tip, {
        children: _jsx(_components.p, {
          children: "If your FTP user has a Home Folder other than the root directory, enter the\nfile location relative to the FTP user's home folder in your Hightouch\ndestination configuration."
        })
      }), "\n", _jsx(_components.h4, {
        id: "google-cloud-storage",
        children: "Google Cloud Storage"
      }), "\n", _jsx(_components.p, {
        children: "To use the Google Cloud Storage file location in SFMC you will need:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Google Cloud Storage bucket"
        }), "\n", _jsxs(_components.li, {
          children: ["Google Service Account with ", _jsx(_components.strong, {
            children: "read"
          }), " access to your bucket. This will be used by SFMC to read from your bucket."]
        }), "\n", _jsxs(_components.li, {
          children: ["Google Service Account with ", _jsx(_components.strong, {
            children: "write"
          }), " access to your bucket. This will be used by Hightouch to upload files."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["If you do not have a Google Service Account, you can follow the setup instructions in our ", _jsx(_components.a, {
          href: "/security/gcp",
          children: "Google Cloud Platform (GCP) documentation"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "To set up a Google Cloud Storage file location in SFMC:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to ", _jsx(_components.strong, {
            children: "Setup → Data Management → File Locations"
          }), " and create a new file location."]
        }), "\n", _jsx(_components.li, {
          children: "Configure your file location's name and external key."
        }), "\n", _jsx(_components.li, {
          children: "Choose Google Cloud Storage for Location Type."
        }), "\n", _jsx(_components.li, {
          children: "Enter your GCS bucket name."
        }), "\n", _jsxs(_components.li, {
          children: ["If you would like for files to be uploaded to a specific path in your bucket, enter the path as the ", _jsx(_components.strong, {
            children: "Relative Location"
          }), " in your file location."]
        }), "\n", _jsx(_components.li, {
          children: "Upload your Google Service Account credentials. Ensure that at a minimum this role has read access to your bucket."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-sfmc-gcs-file-location.png",
          alt: "Google Cloud Storage file location setup"
        })
      }), "\n", _jsx(_components.p, {
        children: "In Hightouch, select the Cloud Credentials you'd like to use to write to your bucket. Ensure this account has write access to your bucket.\nEnter your file location External Key, GCP project ID, bucket name, and relative path."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-sfmc-gcs-file-location-form.png",
          alt: "Google Cloud Storage file location in Hightouch"
        })
      }), "\n", _jsx(_components.p, {
        children: "Test your connection to ensure you've successfully connected to SFMC's API, FTP, and your GCS bucket."
      }), "\n", _jsx(_components.h4, {
        id: "amazon-s3",
        children: "Amazon S3"
      }), "\n", _jsx(_components.p, {
        children: "To use the Amazon S3 file location in SFMC you will need:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Amazon S3 bucket"
        }), "\n", _jsxs(_components.li, {
          children: ["AWS Credentials with ", _jsx(_components.strong, {
            children: "read"
          }), " access to your bucket. This will be used by SFMC to read from your bucket."]
        }), "\n", _jsxs(_components.li, {
          children: ["AWS Credentials with ", _jsx(_components.strong, {
            children: "write"
          }), " access to your bucket. This will be used by Hightouch to upload files."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["If you do not have AWS Credentials, you can follow the setup instructions in our ", _jsx(_components.a, {
          href: "/security/aws",
          children: "AWS documentation"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "To set up an AWS S3 file location in SFMC:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to ", _jsx(_components.strong, {
            children: "Setup → Data Management → File Locations"
          }), " and create a new file location."]
        }), "\n", _jsx(_components.li, {
          children: "Configure your file location's name and external key."
        }), "\n", _jsx(_components.li, {
          children: "Choose Amazon Simple Storage Service for Location Type."
        }), "\n", _jsx(_components.li, {
          children: "Choose whether you'd like to authenticate with an Access Key or Access Key with IAM role and enter your AWS credentials. Ensure that at a minimum this account has read access to your bucket."
        }), "\n", _jsx(_components.li, {
          children: "Enter your S3 bucket name."
        }), "\n", _jsxs(_components.li, {
          children: ["If you would like for files to be uploaded to a specific path in your bucket, enter the path as the ", _jsx(_components.strong, {
            children: "AWS Relative Location"
          }), " in your file location."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-sfmc-s3-file-location.png",
          alt: "Amazon S3 file location setup"
        })
      }), "\n", _jsx(_components.p, {
        children: "In Hightouch, select the Cloud Credentials you'd like to use to write to your bucket. Ensure this account has write access to your bucket.\nEnter your file location External Key, AWS region, bucket name, and relative path."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-sfmc-s3-file-location-form.png",
          alt: "Amazon S3 file location in Hightouch"
        })
      }), "\n", _jsx(_components.p, {
        children: "Test your connection to ensure you've successfully connected to SFMC's API, FTP, and your S3 bucket."
      }), "\n", _jsx(_components.h3, {
        id: "custom-scopes",
        children: "Custom scopes"
      }), "\n", _jsx(Tip, {
        children: _jsx(_components.p, {
          children: "This section is for advanced users who need specific permission scopes for\ntheir integration."
        })
      }), "\n", _jsxs(_components.p, {
        children: ["By default, Hightouch requests permission for Journeys, Contacts, and Data\nExtensions. If you want to use a subset of these features, you can grant a\ncustom subset of scopes in your SFMC package. After you do this, enter a\nspace-separated list of scopes in the ", _jsx(_components.strong, {
          children: "Custom scopes"
        }), " field in the\nHightouch destination configuration."]
      }), "\n", _jsx(_components.p, {
        children: "For example, if you want to sync to Data Extensions via the SOAP API, you\ncan grant the following scopes in your SFMC package:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Data > Data Extensions"
          }), ": Read, Write"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Provisioning > Accounts"
          }), ": Read"]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Then, enter the following scopes in the Hightouch destination configuration:"
      }), "\n", _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "hljs",
          children: "accounts_read data_extensions_read data_extensions_write\n"
        })
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-sfmc-custom-scopes.png",
          alt: "Custom scopes in the Hightouch UI"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["See the ", _jsx(_components.a, {
          href: "https://developer.salesforce.com/docs/marketing/marketing-cloud/references/mc_rest_rest_permission_scopes/rest-permissions-and-scopes.html",
          children: "SFMC docs for a full list of\nscopes"
        }), "."]
      }), "\n", _jsx(Warning, {
        children: _jsx(_components.p, {
          children: "The scopes listed in Hightouch can't exceed the scopes granted in your SFMC\npackage. If you attempt to use scopes not granted in SFMC, your authentication\nwill fail."
        })
      }), "\n", _jsxs(_components.p, {
        children: ["If you import SFMC email templates through ", _jsx(_components.a, {
          href: "/context-hub/connections",
          children: "Context Hub"
        }), ", template import also requests only the scopes in ", _jsx(_components.strong, {
          children: "Custom scopes"
        }), ". Add ", _jsx(_components.code, {
          children: "email_read"
        }), " and ", _jsx(_components.code, {
          children: "documents_and_images_read"
        }), " to the field, after you grant them in your SFMC package."]
      }), "\n", _jsx(_components.h3, {
        id: "allowed-business-units",
        children: "Allowed Business Units"
      }), "\n", _jsx(Tip, {
        children: _jsx(_components.p, {
          children: "This section is relevant for SFMC instances with multiple Business Units."
        })
      }), "\n", _jsxs(_components.p, {
        children: ["By default, Hightouch can access all Business Units in your SFMC instance that\nhave the installed package enabled. If you want to restrict access to a subset\nof Business Units, you can enter a space-separated list of Business Unit IDs in\nthe ", _jsx(_components.strong, {
          children: "Allowed business unit IDs"
        }), " field in the Hightouch destination\nconfiguration."]
      }), "\n", _jsx(_components.p, {
        children: "You can find the Business Unit ID, or MID, by clicking on the account dropdown at\nthe top of the SFMC console."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-sfmc-mid.png",
          alt: "Finding MID in SFMC"
        })
      }), "\n", _jsx(_components.h2, {
        id: "use-sfmc-with-agents",
        children: "Use SFMC with agents"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.a, {
          href: "/agents/overview",
          children: "Agents"
        }), " can read the campaigns, journeys, and email content you've built in SFMC when you connect SFMC in ", _jsx(_components.strong, {
          children: "Context Hub > Connected data"
        }), ". Context Hub uses the credentials from this destination, so you don't enter them again."]
      }), "\n", _jsxs(_components.p, {
        children: ["The installed package needs more read permissions for this than syncing does. For the full list, see ", _jsx(_components.a, {
          href: "/context-hub/connections#connect-advertising-and-lifecycle-platforms",
          children: "Connect advertising and lifecycle platforms"
        }), ". If the package is missing a permission, connecting fails with a ", _jsxs(_components.a, {
          href: "#scope_mismatch-when-reading-campaigns-or-templates",
          children: [_jsx(_components.code, {
            children: "scope_mismatch"
          }), " error"]
        }), "."]
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've set up your Salesforce Marketing Cloud destination and have a ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), " to pull data from, you can set up your sync configuration to begin syncing data. Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/syncs?from=docs",
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add sync"
        }), " button to begin. Then, select the relevant model and the Salesforce Marketing Cloud destination you want to sync to."]
      }), "\n", _jsx(_components.p, {
        children: "The following sections outline the different types of syncs you can configure with SFMC:"
      }), "\n", _jsx(_components.h3, {
        id: "data-extensions",
        children: "Data Extensions"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch supports syncing objects to existing Data Extensions and creating new ones.\nYou can select either ", _jsx(_components.strong, {
          children: "Upsert"
        }), " or ", _jsx(_components.strong, {
          children: "Mirror"
        }), " mode."]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["In ", _jsx(_components.strong, {
            children: "Upsert"
          }), " mode, you can choose whether to use the SOAP API or ", _jsx(_components.a, {
            href: "#automatic-file-import-via-file-uploads",
            children: "file uploads"
          }), ". Hightouch inserts new objects into the Data Extension and keeps all mapped object attributes up-to-date."]
        }), "\n", _jsxs(_components.li, {
          children: ["In ", _jsx(_components.strong, {
            children: "Mirror"
          }), " mode, you can't select between SOAP API or file uploads. Hightouch always uses file uploads, SOAP, and an Overwrite import automation in SFMC to overwrite the entire Data Extension with your model's query results."]
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "optional-configurations",
        children: "Optional configurations"
      }), "\n", _jsxs(_components.p, {
        children: ["You can optionally choose which ", _jsx(_components.strong, {
          children: "Business Unit"
        }), " and which ", _jsx(_components.strong, {
          children: "Data Folder"
        }), " you would you like to sync data to. If you leave these configuration empty, Hightouch uses the defaults configured in SFMC."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-sfmc-optional-configurations.png",
          alt: "Data extension configuration in the Hightouch UI"
        })
      }), "\n", _jsx(_components.h4, {
        id: "automatic-data-extension-creation",
        children: "Automatic Data Extension creation"
      }), "\n", _jsxs(_components.p, {
        children: ["You can optionally choose whether Hightouch should ", _jsx(_components.strong, {
          children: "automatically create a Data Extension"
        }), ". If you enable automatic creation, Hightouch creates a Data Extension the first time the sync runs. It doesn't create new Data Extensions on subsequent runs. If you add columns to the sync config, Hightouch automatically adds them to the Data Extension."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-sfmc-data-extension-auto-creation.png",
          alt: "Data extension configuration in the Hightouch UI"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["When creating a new extension, you can optionally specify the Data Extension's name.\nIf you leave the name blank, Hightouch uses the model name.\nThe name also becomes the Data Extension's ", _jsx(_components.strong, {
          children: "customer key"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["You can include timestamp variables in the Data Extension name, surrounding each with ", _jsx(_components.code, {
          children: "{}"
        }), ".\nHightouch supports these timestamp variables:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "YYYY"
          }), ": Represents the full year in four digits."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "YY"
          }), ": The last two digits of the year."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "MM"
          }), ": Two-digit month format (01-12)."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "DD"
          }), ": Two-digit day format (01-31)."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "HH"
          }), ": Two-digit hour format in 24-hour clock (00-23)."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "mm"
          }), ": Two-digit minute format (00-59)."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "ss"
          }), ": Two-digit second format (00-59)."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "ms"
          }), ": Three-digit millisecond format."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "X"
          }), ": Unix timestamp in seconds."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "x"
          }), ": Unix timestamp in milliseconds."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "All dates and times are UTC."
      }), "\n", _jsxs(_components.p, {
        children: ["You can also use other ", _jsx(_components.a, {
          href: "/syncs/mapping-data#variable-values",
          children: "variable values"
        }), " to include sync metadata in the Data Extension name:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "{model.id}"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "{model.name}"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "{sync.id}"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "{sync.run.id}"
          })
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "To change the name or customer key, you need to create a new sync. Hightouch doesn't modify the Data Extension's name or customer key after the first run."
      }), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["You can also optionally enter a file path when creating a Data Extension, such\nas ", _jsx(_components.code, {
            children: "path/to/dataExtension"
          }), ". If you choose a file path that includes folders,\nHightouch will only create new folders if they do not already exist, relative\nto the selected folder in the Data Folder option. This path can also include\nthe variable values mentioned above, such as ", _jsx(_components.code, {
            children: "{YYYY}/{MM}/{DD}/{model.name}"
          }), "."]
        })
      }), "\n", _jsx(_components.h4, {
        id: "automatic-file-import-via-file-uploads",
        children: "Automatic file import via file uploads"
      }), "\n", _jsx(_components.p, {
        children: "By default, Hightouch uses the SOAP API to sync Data Extension options in upsert mode. If you select to use File Upload mode, Hightouch uploads records via file transfer to SFMC, which may lead to better sync performance. While using File Upload mode is generally faster, it provides less visibility into individual records' sync successes and failures."
      }), "\n", _jsx(_components.p, {
        children: "Generally, blob storage file locations (such as Google Cloud Storage and Amazon S3) are more performant file servers than SFMC's default option of ExactTarget Enhanced FTP server."
      }), "\n", _jsx(_components.p, {
        children: "To use File Upload mode, ensure:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["your SFMC package has ", _jsx(_components.a, {
            href: "#create-a-server-to-server-package",
            children: "the appropriate scopes"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["you've ", _jsx(_components.a, {
            href: "#create-ftp-user",
            children: "created an FTP user"
          }), " in SFMC"]
        }), "\n", _jsxs(_components.li, {
          children: ["you've ", _jsx(_components.a, {
            href: "#connect-to-sfmc",
            children: "authenticated the FTP user"
          }), " in your destination configuration"]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Additionally, if you are using a blob storage file location, ensure:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "your SFMC file location has read access to your bucket"
        }), "\n", _jsx(_components.li, {
          children: "the credentials you've provided Hightouch has write access to your bucket."
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["When using File Upload mode, Hightouch creates and runs an ", _jsx(_components.a, {
          href: "https://help.salesforce.com/s/articleView?id=sf.mc_es_import_activity.htm&type=5",
          children: "import definition"
        }), " to load the data into the Data Extension. By default, Hightouch includes 100,000 rows in each file. You can configure this batch size in the Hightouch UI—the maximum value is 10 million rows per file. After each batch, Hightouch processes the SFMC results file to determine row errors."]
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "SFMC provides more detailed error responses when using the SOAP API. If you\nneed to debug errors and the error messages from File Upload mode aren't\nhelpful, we recommend trying the SOAP API."
        })
      }), "\n", _jsx(Warning, {
        children: _jsxs(_components.p, {
          children: ["If you enable ", _jsx(_components.a, {
            href: "#delete-behavior",
            children: "deletion"
          }), " in upsert mode, Hightouch removes\ndeleted rows in the Data Extension using the ", _jsx(_components.a, {
            href: "https://developer.salesforce.com/docs/marketing/marketing-cloud/guide/delete.htm",
            children: "SOAP\nAPI"
          }), ",\nsince SFMC doesn't support deletions using FTP directly. If you want to\ncompletely overwrite the Data Extension with the results of your sync, use\n", _jsx(_components.strong, {
            children: "Mirror"
          }), " mode."]
        })
      }), "\n", _jsx(_components.h4, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsxs(_components.p, {
        children: ["You can match objects from your source to your Data Extension on your Data Extension's ", _jsx(_components.a, {
          href: "https://help.salesforce.com/s/articleView?id=000388307&type=1",
          children: "primary key"
        }), ".\nSelect the model column that contains these values."]
      }), "\n", _jsx(Warning, {
        children: _jsx(_components.p, {
          children: "Editing the Data Extension field used for record matching is only possible\nbefore triggering the first sync run. Otherwise, making this change results in\nan error. If you want to use a different field once the sync has already run,\nyou need to create a new sync."
        })
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsxs(_components.p, {
        children: ["You can map any model column to any field in your Data Extension.\nMake sure that you map all ", _jsx(_components.strong, {
          children: "non-nullable fields"
        }), " when syncing new objects to the Data Extension.\nA non-nullable field is a field that is required to be populated with a value.\nWhen creating or editing a record in SFMC, if a non-nullable field is left empty, the system won't allow the record to be saved and will return an error message."]
      }), "\n", _jsx(_components.p, {
        children: "You can check which fields are non-nullable fields in SFMC by inspecting your Data Extensions:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "Open the Email Studio in your SFMC account."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-sfmc-email-extension.png",
          alt: "Inspecting Data Extensions in SFMC"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "2",
        children: ["\n", _jsx(_components.li, {
          children: "Select the Data Extension you want to check."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-sfmc-data-extension-inspection.png",
          alt: "Inspecting Data Extensions in SFMC"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "3",
        children: ["\n", _jsxs(_components.li, {
          children: ["Inspect the ", _jsx(_components.strong, {
            children: "Fields"
          }), " section in the Data Extension to see a list of all fields."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-sfmc-data-extension-nullable-fields.png",
          alt: "Inspecting Data Extensions in SFMC"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "4",
        children: ["\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Look for the ", _jsx(_components.strong, {
              children: "Nullable"
            }), " column in the list. This column includes a checkbox for each field."]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["If a field doesn't have a check in the ", _jsx(_components.strong, {
              children: "Nullable"
            }), " column, it means that the field is non-nullable and must have a value in it when a record is created or updated. Make sure you map all non-nullable fields."]
          }), "\n"]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["In the example screenshot below, since ", _jsx(_components.code, {
          children: "email"
        }), " is the only field without a check in the Nullable column, it is the only non-nullable field."]
      }), "\n", _jsxs(_components.p, {
        children: ["In ", _jsx(_components.strong, {
          children: "Upsert"
        }), " mode, you can sync records that don't include all non-nullable fields, but only if the object already exists in the Data Extension."]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["SFMC requires a minimum of one field mapping when sending data using\n", _jsx(_components.em, {
            children: "SOAP"
          }), "-mode. This can be a placeholder field."]
        })
      }), "\n", _jsx(_components.h4, {
        id: "field-creation",
        children: "Field creation"
      }), "\n", _jsx(_components.p, {
        children: "When creating a new Data Extension with Hightouch, you can create new fields and select their types while field mapping. First enter a field name, then select its type."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-sfmc-data-extension-field-creation.png",
          alt: "Data extension configuration in the Hightouch UI"
        })
      }), "\n", _jsx(_components.p, {
        children: "Text and Decimal field types allow for additional configuration."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-sfmc-data-extension-field-creation-2.png",
          alt: "Data extension configuration in the Hightouch UI"
        })
      }), "\n", _jsx(_components.p, {
        children: "Hightouch adds new fields to the Data Extension on each run but doesn't remove fields that leave the model's query results."
      }), "\n", _jsx(_components.h4, {
        id: "sendable-and-testable-data-extensions",
        children: "Sendable and testable Data Extensions"
      }), "\n", _jsxs(_components.p, {
        children: ["For auto-created Data Extensions, you can select whether the Data Extension is ", _jsx(_components.strong, {
          children: "sendable"
        }), " and ", _jsx(_components.strong, {
          children: "testable"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "A sendable Data Extension is a type of Data Extension that can be used to send email messages or other types of communications to subscribers. Sendable Data Extensions are designed to contain subscriber data that can be used in email marketing campaigns, triggered sends, or other types of communications."
      }), "\n", _jsx(_components.p, {
        children: "A testable Data Extension is used to send test emails or other test communications to a small group of recipients, usually internal stakeholders or quality assurance testers. Testable Data Extensions are designed to mimic the structure of a sendable Data Extension, but with a smaller set of test data."
      }), "\n", _jsx(_components.p, {
        children: "To be designated as sendable, a Data Extension must meet certain requirements, including:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "It must contain an email address or mobile number field that is marked as the primary sendable field."
        }), "\n", _jsx(_components.li, {
          children: "It must contain at least one additional field that is used as a personalization field in the email message."
        }), "\n", _jsx(_components.li, {
          children: "It must be mapped to a profile attribute group, which is used to store additional subscriber data that is not included in the sendable Data Extension."
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["To make the Data Extension you are syncing via Hightouch sendable, select the field to use as the primary sendable field and the field to map to a subscriber field. The options available for the sendable field are ", _jsx(_components.a, {
          href: "#field-mapping-2",
          children: "the fields you mapped"
        }), "."]
      }), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["The length of the field chosen in send relationships is ", _jsx(_components.a, {
            href: "https://help.salesforce.com/s/articleView?id=sf.mc_rn_2024_spring_cab_de_restriction.htm&type=5",
            children: "limited to 254\ncharacters"
          }), ".\nChoose the ", _jsx(_components.code, {
            children: "Text - 254 characters"
          }), " field type when creating a sendable field\nin Hightouch to enforce this limit."]
        })
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-sfmc-data-extension-sendable.png",
          alt: "Data extension configuration in the Hightouch UI"
        })
      }), "\n", _jsx(_components.p, {
        children: "You can only set a Data Extension to be testable once you select it to be sendable."
      }), "\n", _jsx(_components.h4, {
        id: "data-retention-policy",
        children: "Data Retention Policy"
      }), "\n", _jsxs(_components.p, {
        children: ["You can set a data retention policy on your Data Extension through Hightouch. Hightouch will ", _jsx(_components.strong, {
          children: "not"
        }), " edit existing data policies unless explicitly enabled."]
      }), "\n", _jsx(_components.p, {
        children: "You can choose what data should be deleted in your policy:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Behavior"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "All records and Data Extension"
              })
            }), _jsx(_components.td, {
              children: "All records and the Data Extension will be deleted at the end of your data retention period"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "All records"
              })
            }), _jsx(_components.td, {
              children: "All records will be deleted at the end of your data retention period, but the Data Extension will not be deleted"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Individual records"
              })
            }), _jsx(_components.td, {
              children: "Records will be deleted from your Data Extension individually as they reach the Data Extension period. The Data Extension will not be deleted."
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: "You can choose the data retention period in days depending on how long you'd like to store your Data Extension and records.\nIf you choose to reset the retention policy upon each successful sync run, the data retention period will restart whenever data is successfully imported into your Data Extension."
      }), "\n", _jsx(Warning, {
        children: _jsx(_components.p, {
          children: "Data retention policies cannot be removed through Hightouch. To remove an\nexisting data retention policy, navigate to your Data Extension in SFMC\nContact Builder and turn off the data retention policy."
        })
      }), "\n", _jsx(_components.h4, {
        id: "delete-behavior",
        children: "Delete behavior"
      }), "\n", _jsx(_components.p, {
        children: "You can choose how to handle Data Extension when the corresponding rows are deleted in your source."
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Behavior"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Do nothing"
              })
            }), _jsx(_components.td, {
              children: "Keep the object in SFMC"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Delete the record"
              })
            }), _jsx(_components.td, {
              children: "Remove the SFMC record completely"
            })]
          })]
        })]
      }), "\n", _jsx(_components.h4, {
        id: "concurrency",
        children: "Concurrency"
      }), "\n", _jsx(_components.p, {
        children: "When syncing to SFMC in File Upload mode, you can configure the concurrency used in the FTP upload to SFMC in Advanced options. Enter a value between 1 and 10.\nHigher concurrency values will increase upload speeds, but can cause disruptions when simultaneously running multiple Hightouch syncs to SFMC. For this reason, the default concurrency is 1."
      }), "\n", _jsx(_components.p, {
        children: "Concurrency options are not available for blob storage file locations."
      }), "\n", _jsx(_components.h4, {
        id: "file-retention",
        children: "File Retention"
      }), "\n", _jsx(_components.p, {
        children: "By default, Hightouch will delete the CSV files used to populate a Data Extension and the results file from the Import Definition automation from your SFMC FTP server. You can choose to keep files on your SFMC FTP server in Advanced options. Files on the SFMC FTP server will automatically be deleted by SFMC after 21 days."
      }), "\n", _jsx(_components.p, {
        children: "Files are not automatically deleted from your blob storage file locations. We recommend setting a retention policy on your bucket to avoid storing stale CSV files."
      }), "\n", _jsx(_components.h4, {
        id: "notifications",
        children: "Notifications"
      }), "\n", _jsx(_components.p, {
        children: "If you would like to receive an email notification when an Import Definition to your Data Extension succeeds, you can enter an email address in Advanced options.\nIn Mirror mode, one email will be sent for each sync run. In Upsert mode, one email will be sent for each batch completion. Depending on your batch size, this could be multiple emails per sync run."
      }), "\n", _jsx(_components.h4, {
        id: "triggering-automations",
        children: "Triggering automations"
      }), "\n", _jsxs(_components.p, {
        children: ["In Mirror mode, if you would like to trigger an automation after the completion of your Data Extension sync run, you can select ", _jsx(_components.strong, {
          children: "Yes"
        }), " or ", _jsx(_components.strong, {
          children: "No"
        }), " and enter either an ", _jsx(_components.strong, {
          children: "External key"
        }), " or an ", _jsx(_components.strong, {
          children: "Automation ID"
        }), ". If ", _jsx(_components.strong, {
          children: "Yes"
        }), ", an automation will run even if there are failed rows in the sync. If the automation itself fails, the entire sync run is marked as failed."]
      }), "\n", _jsx(_components.h3, {
        id: "contacts",
        children: "Contacts"
      }), "\n", _jsxs(_components.p, {
        children: ["This integration supports upserting contacts into Marketing Cloud. In upsert mode, Hightouch inserts new users into SFMC and updates ", _jsx(_components.a, {
          href: "#field-mapping",
          children: "mapped user attributes"
        }), " on existing contacts."]
      }), "\n", _jsx(_components.h4, {
        id: "record-matching-1",
        children: "Record matching"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch matches rows from your model to Marketing Cloud contacts by ", _jsx(_components.strong, {
          children: "Contact Key"
        }), ". Select the model column that contains these values."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-sfmc-record-matching.png",
          alt: "SFMC Contact sync configuration in the Hightouch UI"
        })
      }), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["Contact Keys are different from Contact IDs and email addresses. Check out\n", _jsx(_components.a, {
            href: "https://trailhead.salesforce.com/content/learn/modules/marketing-cloud-contact-management/understand-contacts-and-contact-model-relationships",
            children: "Salesforce's\narticle"
          }), "\nto learn more."]
        })
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping-1",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "You can sync any model columns to Marketing Cloud's contact attributes."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-sfmc-field-mapping.png",
          alt: "SFMC Contact sync configuration in the Hightouch UI"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Ensure all the required fields in each attribute set is completed. For example, to add an email address, be sure to map both ", _jsx(_components.strong, {
          children: "Email Addresses.Email Address"
        }), " and ", _jsx(_components.strong, {
          children: "Email Addresses.HTML Enabled"
        }), " fields. Refer to the usage section of SFMC's ", _jsx(_components.a, {
          href: "https://developer.salesforce.com/docs/atlas.en-us.noversion.mc-apis.meta/mc-apis/createContacts.htm",
          children: "Creating Contacts"
        }), " documentation for a list of attribute sets."]
      }), "\n", _jsx(_components.h3, {
        id: "journeys",
        children: "Journeys"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch can manage the entry and exit of contacts in Marketing Cloud journeys based on your model's query results."
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["When a record enters your results set, Hightouch adds a contact to the journey via the contact's ", _jsx(_components.a, {
            href: "https://trailhead.salesforce.com/content/learn/modules/marketing-cloud-contact-management/understand-contacts-and-contact-model-relationships",
            children: "Contact Key"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["When a record leaves your results set, Hightouch removes the contact from the journey via the contact's ", _jsx(_components.a, {
            href: "https://trailhead.salesforce.com/content/learn/modules/marketing-cloud-contact-management/understand-contacts-and-contact-model-relationships",
            children: "Contact Key"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "Hightouch does not observe duplicate primary key errors. Adding a record with\na primary key that already exists in the journey will be marked as successful.\nSimilarly, removing a record with a primary key that was already removed from\nthe journey will also be marked as successful."
        })
      }), "\n", _jsx(_components.h4, {
        id: "journey-selection",
        children: "Journey selection"
      }), "\n", _jsx(_components.p, {
        children: "Here is an example journey that you could manage with Hightouch:"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-sfmc-entry-event.png",
          alt: "SFMC Journey example"
        })
      }), "\n", _jsx(_components.p, {
        children: "Note that it's:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "A multistep journey"
        }), "\n", _jsxs(_components.li, {
          children: ["Triggered by an entry event with an associated ", _jsx(_components.strong, {
            children: "Data Extension"
          }), " and ", _jsx(_components.strong, {
            children: "event definition key"
          })]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Hightouch automatically looks for the event definition key if you select a journey that fits the preceding requirements."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-sfmc-select-journey.png",
          alt: "Journey selection in the Hightouch UI"
        })
      }), "\n", _jsx(_components.h4, {
        id: "user-identifiers",
        children: "User identifiers"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch enters and exits contacts from the journey via ", _jsx(_components.strong, {
          children: "Contact Key"
        }), ". Select the model column that contains these values."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-sfmc-record-matching.png",
          alt: "SFMC Contact sync configuration in the Hightouch UI"
        })
      }), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["Contact Keys are different from Contact IDs and email addresses. Check out\n", _jsx(_components.a, {
            href: "https://trailhead.salesforce.com/content/learn/modules/marketing-cloud-contact-management/understand-contacts-and-contact-model-relationships",
            children: "Salesforce's\narticle"
          }), "\nto learn more."]
        })
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping-2",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "You can use field mapping to map any other columns in your query results to the Data Extension containing your journey's data."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-sfmc-attributes.png",
          alt: "Journey configuration in the Hightouch UI"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["The Data Extension requires the ", _jsx(_components.strong, {
          children: "email_address"
        }), " field—be sure to map a model column to that attribute. You can do this in Hightouch by typing \"email_address\" as a ", _jsx(_components.strong, {
          children: "Field from Salesforce Marketing Cloud"
        }), " in the mappings section and then selecting the appropriate model column."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-sfmc-map-email.png",
          alt: "Journey configuration in the Hightouch UI"
        })
      }), "\n", _jsx(_components.h3, {
        id: "triggered-send",
        children: "Triggered send"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch can trigger emails to individual subscribers via the Marketing Cloud API. This is useful for sending personalized messages to specific users based on their behavior or attributes."
      }), "\n", _jsxs(_components.p, {
        children: ["Select an existing Triggered Send definition, then map the model columns to the Triggered Send's attributes, including email addresses and custom attributes. Hightouch matches rows from your model to Marketing Cloud contacts by ", _jsx(_components.strong, {
          children: "Subscriber Key"
        }), "."]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["For Hightouch platform error codes related to SFMC, see ", _jsx(_components.a, {
            href: "/developer-tools/error-codes#salesforce-marketing-cloud-sfmc",
            children: "Error codes:\nSFMC"
          }), "."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "validate-your-setup",
        children: "Validate your setup"
      }), "\n", _jsx(_components.p, {
        children: "After you configure your sync, run a test to confirm data flows correctly:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "Start with a small test audience or a single record."
        }), "\n", _jsxs(_components.li, {
          children: ["Trigger a manual sync from the ", _jsx(_components.a, {
            href: "https://app.hightouch.com/syncs?from=docs",
            children: "Syncs overview"
          }), " page."]
        }), "\n", _jsxs(_components.li, {
          children: ["After the sync completes, verify the data in SFMC:", "\n", _jsxs(_components.ul, {
            children: ["\n", _jsxs(_components.li, {
              children: ["For Data Extension syncs, open the target Data Extension in ", _jsx(_components.strong, {
                children: "Audience Builder → Contact Builder → Data Extensions"
              }), " and confirm your records appear with the expected field values."]
            }), "\n", _jsxs(_components.li, {
              children: ["For Contact syncs, search for the test contact in ", _jsx(_components.strong, {
                children: "Contact Builder"
              }), " and verify attributes."]
            }), "\n", _jsxs(_components.li, {
              children: ["For Triggered Send syncs, check the ", _jsx(_components.strong, {
                children: "Tracking"
              }), " tab of the Triggered Send to confirm delivery."]
            }), "\n"]
          }), "\n"]
        }), "\n", _jsx(_components.li, {
          children: "If the sync shows errors, check the sync run log in Hightouch for specific API error messages."
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "choosing-a-sync-method-for-data-extensions",
        children: "Choosing a sync method for Data Extensions"
      }), "\n", _jsxs(_components.p, {
        children: ["When syncing ", _jsx(_components.a, {
          href: "#data-extensions",
          children: "Data Extensions"
        }), ", three sync methods are available. Choose based on your volume and debugging needs:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Upsert via SOAP API"
          }), " — Best for smaller datasets or when you need detailed per-row error messages. The SOAP API is limited to approximately 2,000 requests per minute, so large syncs can be slow."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsxs(_components.strong, {
            children: ["Upsert via ", _jsx(_components.a, {
              href: "#automatic-file-import-via-file-uploads",
              children: "file uploads"
            })]
          }), " — Best for large datasets (over 1 million records). Hightouch uploads records via SFTP, which is significantly faster than SOAP. Error messages are less granular."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: _jsx(_components.a, {
              href: "#data-extensions",
              children: "Mirror"
            })
          }), " — Best when you need to fully replace the Data Extension contents each sync, such as heavy deletion use cases. Mirror always uses file uploads."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "If you're unsure, start with upsert via file uploads for production syncs and switch to SOAP temporarily when debugging row-level errors."
      }), "\n", _jsx(_components.h3, {
        id: "datetime-timezone-conversions",
        children: "DateTime timezone conversions"
      }), "\n", _jsxs(_components.p, {
        children: ["SFMC converts all incoming datetime values to UTC and displays them in the account's configured timezone. This can cause apparent date shifts — for example, a timestamp of ", _jsx(_components.code, {
          children: "2026-06-02 22:00:00 America/Los_Angeles"
        }), " is stored as ", _jsx(_components.code, {
          children: "2026-06-03 05:00:00 UTC"
        }), ", which may display as June 3 instead of June 2."]
      }), "\n", _jsx(_components.p, {
        children: "To avoid this:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Add a transformed column in your data source that includes explicit timezone information (for example, an ISO 8601 string with a timezone offset like ", _jsx(_components.code, {
            children: "2026-06-02T22:00:00-07:00"
          }), ")."]
        }), "\n", _jsxs(_components.li, {
          children: ["Alternatively, use a timezone conversion function in your model query. For example, in Snowflake: ", _jsx(_components.code, {
            children: "CONVERT_TIMEZONE('America/Los_Angeles', 'UTC', your_timestamp_column)"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(CommonErrorsPreface, {}), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              align: "left",
              children: "Error message"
            }), _jsx(_components.th, {
              align: "left",
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "Failed to create Data Extension"
              })
            }), _jsxs(_components.td, {
              align: "left",
              children: ["Incomplete sync configuration with missing field names or types. ", _jsx(_components.a, {
                href: "#failed-to-create-data-extension",
                children: "More detail."
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "Instance validation error: ... is not a valid value for DataExtensionFieldType"
              })
            }), _jsxs(_components.td, {
              align: "left",
              children: ["Data Extension name conflicts with an existing one. ", _jsx(_components.a, {
                href: "#instance-validation-error-data-type-value-is-not-a-valid-value-for-dataextensionfieldtype",
                children: "More detail."
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "The event data contains duplicate values for an existing primary key"
              })
            }), _jsxs(_components.td, {
              align: "left",
              children: ["Duplicate values in the primary/subscriber key column. ", _jsx(_components.a, {
                href: "#the-event-data-contains-duplicate-values-for-an-existing-primary-key",
                children: "More detail."
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "The Custom Object field selected must have a type of Number or Long Number"
              })
            }), _jsxs(_components.td, {
              align: "left",
              children: ["Subscriber ID field mapped to a non-numeric type. ", _jsx(_components.a, {
                href: "#the-custom-object-field-selected-must-have-a-type-of-number-or-long-number",
                children: "More detail."
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "Adding a new Data Extension definition is not allowed when doing an update-only operation"
              })
            }), _jsxs(_components.td, {
              align: "left",
              children: ["Data Extension was deleted or name collides with an existing one. ", _jsx(_components.a, {
                href: "#adding-a-new-data-extension-definition-is-not-allowed-when-doing-an-update-only-operation",
                children: "More detail."
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "FileNotFound: Soap Error (import instance could not be validated)"
              })
            }), _jsxs(_components.td, {
              align: "left",
              children: ["SFMC can't access the uploaded file due to FTP credential or directory issues. ", _jsx(_components.a, {
                href: "#filenotfound-soap-error-import-instance-could-not-be-validated",
                children: "More detail."
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "Mirror sync shows failure but most records are present in SFMC"
              })
            }), _jsxs(_components.td, {
              align: "left",
              children: ["A few rows failed validation, marking the entire sync run as failed. ", _jsx(_components.a, {
                href: "#mirror-sync-shows-failure-but-most-records-are-present-in-sfmc",
                children: "More detail."
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsxs(_components.td, {
              align: "left",
              children: [_jsx(_components.code, {
                children: "invalid_client"
              }), " authentication error"]
            }), _jsxs(_components.td, {
              align: "left",
              children: ["The installed package's client secret expired or is invalid. ", _jsx(_components.a, {
                href: "#invalid_client-authentication-error",
                children: "More detail."
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsxs(_components.td, {
              align: "left",
              children: [_jsx(_components.code, {
                children: "scope_mismatch"
              }), " when reading campaigns or templates"]
            }), _jsxs(_components.td, {
              align: "left",
              children: ["The installed package doesn't grant a scope Hightouch requested. ", _jsx(_components.a, {
                href: "#scope_mismatch-when-reading-campaigns-or-templates",
                children: "More detail."
              })]
            })]
          })]
        })]
      }), "\n", _jsx(_components.h4, {
        id: "failed-to-create-data-extension",
        children: "Failed to create Data Extension"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-css",
          children: ["Failed ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "to"
          }), " create Data Extension\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Cause:"
        }), " The sync configuration is incomplete — field names or field types are missing."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Solution:"
        }), " Open your sync configuration and ensure you've entered the field names and selected the field types when ", _jsx(_components.a, {
          href: "#field-creation",
          children: "creating new fields"
        }), "."]
      }), "\n", _jsx(_components.h4, {
        id: "instance-validation-error-data-type-value-is-not-a-valid-value-for-dataextensionfieldtype",
        children: "Instance validation error: 'DATA TYPE VALUE' is not a valid value for DataExtensionFieldType"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-vbnet",
          children: ["Server was unable ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "to"
          }), " read request. ---> There ", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "is"
          }), " an ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "error"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "in"
          }), " the XML document. ... ", _jsx(_components.span, {
            className: "hljs-comment",
            children: "'DATA TYPE VALUE' is not a valid value for DataExtensionFieldType"
          }), "\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Cause:"
        }), " The sync is configured to create a new Data Extension with the same name as one that already exists in SFMC."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Solution:"
        }), " Change the name of the Data Extension in your sync configuration to a unique name, and select field types for each mapped column."]
      }), "\n", _jsx(_components.h4, {
        id: "the-event-data-contains-duplicate-values-for-an-existing-primary-key",
        children: "The event data contains duplicate values for an existing primary key"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-json",
          children: [_jsx(_components.span, {
            className: "hljs-number",
            children: "400"
          }), " - ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"message\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), _jsx(_components.span, {
            className: "hljs-string",
            children: "\"The event data contains duplicate value for an existing primary key. Please correct the event data and try again.\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"errorcode\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), _jsx(_components.span, {
            className: "hljs-number",
            children: "30000"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"documentation\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), _jsx(_components.span, {
            className: "hljs-string",
            children: "\"\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Cause:"
        }), " The column selected as the primary or subscriber key contains duplicate values."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Solution:"
        }), " Ensure the key column doesn't contain duplicates. Update your ", _jsx(_components.a, {
          href: "/models/creating-models",
          children: "model definition"
        }), " to deduplicate if needed. See ", _jsx(_components.a, {
          href: "#data-extensions",
          children: "syncing Data Extensions"
        }), " for more on key configuration."]
      }), "\n", _jsx(_components.h4, {
        id: "the-custom-object-field-selected-must-have-a-type-of-number-or-long-number",
        children: "The Custom Object field selected must have a type of Number or Long Number"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-typescript",
          children: [_jsx(_components.span, {
            className: "hljs-title class_",
            children: "The"
          }), " ", _jsx(_components.span, {
            className: "hljs-title class_",
            children: "Custom"
          }), " ", _jsx(_components.span, {
            className: "hljs-title class_",
            children: "Object"
          }), " field selected must have a ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "type"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "of"
          }), " ", _jsx(_components.span, {
            className: "hljs-title class_",
            children: "Number"
          }), " or ", _jsx(_components.span, {
            className: "hljs-title class_",
            children: "Long"
          }), " ", _jsx(_components.span, {
            className: "hljs-title class_",
            children: "Number"
          }), "\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Cause:"
        }), " You chose to make the Data Extension sendable and set the subscriber relationship using the Subscriber ID, but the mapped field isn't a numeric type."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Solution:"
        }), " Either map a numeric field to the Subscriber ID, or map the field to the Subscriber Key instead. See SFMC's ", _jsx(_components.a, {
          href: "https://help.salesforce.com/s/articleView?id=sf.mc_as_data_view_subscribers.htm&type=5",
          children: "Subscriber data view documentation"
        }), " for field type requirements."]
      }), "\n", _jsx(_components.h4, {
        id: "adding-a-new-data-extension-definition-is-not-allowed-when-doing-an-update-only-operation",
        children: "Adding a new Data Extension definition is not allowed when doing an update-only operation"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-vbnet",
          children: ["Could ", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "not"
          }), " create data extension fields: ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "Error"
          }), ": Soap ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "Error"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "Key"
          }), ": ... Adding a ", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "new"
          }), " Data Extension definition ", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "is"
          }), " ", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "not"
          }), " allowed ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "when"
          }), " doing an update-only operation\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Cause:"
        }), " Either the Data Extension set in the sync configuration has been deleted from SFMC, or the sync is set to ", _jsx(_components.a, {
          href: "#automatic-data-extension-creation",
          children: "automatically create a new Data Extension"
        }), " with a name that matches an existing Data Extension."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Solution:"
        }), " Ensure your sync sends data to an existing Data Extension. If the sync is creating a new Data Extension, use a unique name that doesn't already exist in your SFMC environment."]
      }), "\n", _jsx(_components.h4, {
        id: "filenotfound-soap-error-import-instance-could-not-be-validated",
        children: "FileNotFound: Soap Error (import instance could not be validated)"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-vbnet",
          children: [_jsx(_components.span, {
            className: "hljs-symbol",
            children: "FileNotFound:"
          }), " Soap ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "Error"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "Key"
          }), ": ... import instance could ", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "not"
          }), " be validated\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Cause:"
        }), " SFMC can't access or find the file uploaded by Hightouch. This typically points to FTP credential or directory configuration issues."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "Solution:"
        })
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "Verify the FTP credentials in your destination configuration are correct. Test them by connecting to the SFTP outside of Hightouch."
        }), "\n", _jsx(_components.li, {
          children: "Ensure the FTP user has access to the correct file location. If you have multiple business units, create the FTP user in the parent business unit or the same business unit as the target Data Extension."
        }), "\n", _jsxs(_components.li, {
          children: ["Check the FTP user's home directory in SFMC. If it's set to something other than ", _jsx(_components.code, {
            children: "/"
          }), ", files may be written to a nested directory instead of the ", _jsx(_components.code, {
            children: "Import/"
          }), " directory. Set the home directory to ", _jsx(_components.code, {
            children: "/"
          }), " (the default)."]
        }), "\n", _jsx(_components.li, {
          children: "If you're using a blob-storage file location (AWS S3 or GCS), ensure the configured role has read permissions."
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "mirror-sync-shows-failure-but-most-records-are-present-in-sfmc",
        children: "Mirror sync shows failure but most records are present in SFMC"
      }), "\n", _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "hljs language-arduino",
          children: _jsxs(_components.span, {
            className: "hljs-function",
            children: ["Sync run ", _jsx(_components.span, {
              className: "hljs-title",
              children: "failed"
            }), " ", _jsx(_components.span, {
              className: "hljs-params",
              children: "(Mirror mode)"
            }), "\n"]
          })
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Cause:"
        }), " A small number of records failed validation on the SFMC side — for example, due to data type mismatches or missing required fields. Any rejected rows cause the entire sync run to be marked as failed."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Solution:"
        }), " Check the ", _jsx(_components.strong, {
          children: "Rejected rows"
        }), " tab in the sync run details to identify the specific records that failed. Fix the underlying data issues in your model, then rerun the sync."]
      }), "\n", _jsx(_components.h4, {
        id: "invalid_client-authentication-error",
        children: "invalid_client authentication error"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-json",
          children: [_jsx(_components.span, {
            className: "hljs-number",
            children: "401"
          }), " - ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"error\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), _jsx(_components.span, {
            className: "hljs-string",
            children: "\"invalid_client\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"error_description\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Invalid client ID or client secret\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Cause:"
        }), " The installed package's client secret expired or is invalid. SFMC client secrets expire 180 days after they're generated, and all secrets created before this policy took effect expire on September 30, 2026."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Solution:"
        }), " Rotate the client secret and update it in Hightouch by following the steps in ", _jsx(_components.a, {
          href: "#client-secret-rotation",
          children: "Client secret rotation"
        }), "."]
      }), "\n", _jsx(_components.h4, {
        id: "scope_mismatch-when-reading-campaigns-or-templates",
        children: "scope_mismatch when reading campaigns or templates"
      }), "\n", _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "hljs language-text",
          children: "400 - {\"error\":\"scope_mismatch\",\"error_description\":\"Your integration doesn't include this scope. Update the scopes of your integration in Marketing Cloud Installed Packages.\"}\n"
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Cause:"
        }), " Hightouch requested an OAuth scope that the installed package doesn't grant. This usually happens when you connect SFMC in Context Hub with a package that was set up only for syncing."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Solution:"
        }), " In SFMC, go to ", _jsx(_components.strong, {
          children: "Setup > Apps > Installed Packages"
        }), " and grant the missing permissions to the package's API integration. See ", _jsx(_components.a, {
          href: "/context-hub/connections",
          children: "Context Hub connections"
        }), " for the full list. If the destination's ", _jsx(_components.strong, {
          children: "Custom scopes"
        }), " field lists scopes, add the new ones there too."]
      }), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
